
import { useState, useEffect } from 'react';
import { createInitialStatCards } from '../initialData';
import { StatCardData } from '../types';
import { loadCardsFromStorage, saveCardsToStorage } from '../utils/storage';
import { logStatsData } from '../utils/logger';
import { useToast } from '@/hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';

export const useStatsData = () => {
  const { toast } = useToast();
  const [statCards, setStatCards] = useState<StatCardData[]>(() => {
    // Try to load from localStorage first
    const savedCards = loadCardsFromStorage();
    if (savedCards) {
      logStatsData('Using saved card data from localStorage');
      return savedCards;
    }
    
    // Otherwise use initial data
    logStatsData('Using initial card data');
    // Initialize with default values - we'll fetch actual values later if needed
    return createInitialStatCards(
      100, // totalMeters
      5,   // criticalMeters
      20,  // totalGateways
      2,   // criticalGateways
      false // isLoading
    );
  });
  
  const [deviceDataLoading, setDeviceDataLoading] = useState(false);
  
  // Save cards to localStorage whenever they change
  useEffect(() => {
    saveCardsToStorage(statCards);
  }, [statCards]);
  
  // Add a new stat card
  const addStatCard = (card: Partial<Omit<StatCardData, 'id' | 'order'>>) => {
    // Find highest order
    const maxOrder = Math.max(...statCards.map(card => card.order), 0);
    
    // Create unique ID
    const id = `stat-${card.title?.toLowerCase().replace(/\s+/g, '-') || 'custom'}-${uuidv4().slice(0, 8)}`;
    
    const newCard: StatCardData = {
      id,
      title: card.title || 'New Statistic',
      value: card.value || '0',
      change: card.change || null,
      isIncreasing: card.isIncreasing || null,
      icon: null, // Will be set by the rendering component
      bgClass: card.bgClass || 'bg-blue-500',
      delay: 0,
      order: maxOrder + 1,
      visible: true
    };
    
    setStatCards(prev => [...prev, newCard]);
    
    toast({
      title: "Stat Card Added",
      description: `"${newCard.title}" has been added to your dashboard`
    });
    
    return newCard;
  };
  
  // Update an existing stat card
  const updateStatCard = (id: string, updates: Partial<Omit<StatCardData, 'id' | 'order'>>) => {
    setStatCards(prev => {
      const updatedCards = prev.map(card => {
        if (card.id === id) {
          const updatedCard = { ...card, ...updates };
          return updatedCard;
        }
        return card;
      });
      
      toast({
        title: "Stat Card Updated",
        description: `Card has been updated successfully`
      });
      
      return updatedCards;
    });
  };
  
  // Remove a stat card
  const removeStatCard = (id: string) => {
    const cardToRemove = statCards.find(card => card.id === id);
    if (!cardToRemove) return;
    
    setStatCards(prev => prev.filter(card => card.id !== id));
    
    toast({
      title: "Stat Card Removed",
      description: `"${cardToRemove.title}" has been removed from your dashboard`
    });
  };
  
  return {
    statCards,
    setStatCards,
    deviceDataLoading,
    addStatCard,
    updateStatCard,
    removeStatCard
  };
};
